import type { GlossaryVisual } from "@/lib/glossary-visuals";

/**
 * 用語集の図を明るい版と暗い版の 2 組で出す。出し分けは app.css の
 * .gl-vis-light と .gl-vis-dark が html の data-theme と
 * prefers-color-scheme を見て行うので、React の状態は持たない。
 */

/** 暗い版を持つ図の外枠は、暗い版で白地を外す。持たない図は従来どおり白地。 */
export function glossaryVisualSurfaceClass(visual: GlossaryVisual): string {
  return visual.darkSrc ? "gl-vis-surface" : "bg-white";
}

/**
 * 詳細ページの PC の図の縦横比。紙の高さは語ごとに 400 から 600 まで 20 刻みなので、
 * 枠を図に合わせて低くする。Tailwind が拾えるよう、クラス名は全文を書く。
 */
const DESKTOP_ASPECT_CLASS: Record<number, string> = {
  400: "md:aspect-[960/400]",
  420: "md:aspect-[960/420]",
  440: "md:aspect-[960/440]",
  460: "md:aspect-[960/460]",
  480: "md:aspect-[960/480]",
  500: "md:aspect-[960/500]",
  520: "md:aspect-[960/520]",
  540: "md:aspect-[960/540]",
  560: "md:aspect-[960/560]",
  580: "md:aspect-[960/580]",
};

export function glossaryDesktopAspectClass(visual: GlossaryVisual): string {
  return DESKTOP_ASPECT_CLASS[visual.height] ?? "md:aspect-[8/5]";
}

export function GlossaryVisualPicture({
  visual,
  imgClassName,
  pictureClassName,
}: {
  visual: GlossaryVisual;
  imgClassName?: string;
  pictureClassName?: string;
}) {
  const hasDark = Boolean(visual.darkSrc);
  return (
    <>
      <picture
        className={`${pictureClassName ?? ""}${hasDark ? " gl-vis-light" : ""}`}
      >
        <source
          media="(max-width: 767px)"
          srcSet={visual.mobileSrc}
          width={visual.mobileWidth}
          height={visual.mobileHeight}
        />
        <img
          src={visual.src}
          alt={visual.alt}
          width={visual.width}
          height={visual.height}
          loading="eager"
          decoding="async"
          className={imgClassName}
        />
      </picture>
      {visual.darkSrc && (
        <picture className={`${pictureClassName ?? ""} gl-vis-dark`}>
          <source
            media="(max-width: 767px)"
            srcSet={visual.mobileDarkSrc ?? visual.darkSrc}
            width={visual.mobileWidth}
            height={visual.mobileHeight}
          />
          <img
            src={visual.darkSrc}
            alt={visual.alt}
            width={visual.width}
            height={visual.height}
            loading="lazy"
            decoding="async"
            className={imgClassName}
          />
        </picture>
      )}
    </>
  );
}

/** 一覧カードやサイドバーの小さな図。デスクトップ版だけを使う。 */
export function GlossaryVisualThumb({
  visual,
  alt,
  imgClassName,
  fetchPriority,
}: {
  visual: GlossaryVisual;
  alt: string;
  imgClassName?: string;
  fetchPriority?: "low";
}) {
  const decorative = alt === "";
  const common = {
    alt,
    width: visual.width,
    height: visual.height,
    loading: "lazy" as const,
    decoding: "async" as const,
    ...(decorative ? { "aria-hidden": true as const } : {}),
    ...(fetchPriority ? { fetchPriority } : {}),
  };
  return (
    <>
      <img
        {...common}
        src={visual.src}
        className={`${imgClassName ?? ""}${visual.darkSrc ? " gl-vis-light" : ""}`}
      />
      {visual.darkSrc && (
        <img
          {...common}
          src={visual.darkSrc}
          className={`${imgClassName ?? ""} gl-vis-dark`}
        />
      )}
    </>
  );
}
